import React, { useState, useRef, useEffect, useCallback } from "react";
import JSZip from "jszip";
import "./App.css";

function SvgToPngConverter() {
  // 基础状态
  const [svgContent, setSvgContent] = useState("");
  const [pngDataUrl, setPngDataUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("upload");
  const [autoDownload, setAutoDownload] = useState(false);

  // 转换设置
  const [scale, setScale] = useState(1);
  const [backgroundColor, setBackgroundColor] = useState("transparent");
  const [quality, setQuality] = useState(90);
  const [customBgColor, setCustomBgColor] = useState("#ffffff");
  const [outputFormat, setOutputFormat] = useState("png");
  const [dpi, setDpi] = useState(96);

  // 尺寸信息
  const [originalSize, setOriginalSize] = useState({
    width: 0,
    height: 0,
  });
  const [outputSize, setOutputSize] = useState({ width: 0, height: 0 });

  // 批量处理
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchProgress, setBatchProgress] = useState(0);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  // 历史记录
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // 模板
  const templates = [
    {
      id: 1,
      name: "高清输出",
      scale: 2,
      quality: 100,
      backgroundColor: "white",
      format: "png",
    },
    {
      id: 2,
      name: "网页优化",
      scale: 1,
      quality: 80,
      backgroundColor: "transparent",
      format: "png",
    },
    {
      id: 3,
      name: "打印质量",
      scale: 3,
      quality: 100,
      backgroundColor: "white",
      format: "png",
      dpi: 300,
    },
  ];

  // Refs
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  // 处理文件上传
  const handleFileUpload = (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".svg")) {
      setError("请选择SVG文件");
      return;
    }

    setFileName(file.name.replace(".svg", ""));
    setError("");

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setSvgContent(content);
      parseSvgSize(content);
      addToHistory(file.name, content);
    };
    reader.readAsText(file);
  };

  // 处理批量文件上传
  const handleBatchFileUpload = (files) => {
    const svgFiles = Array.from(files).filter((file) =>
      file.name.toLowerCase().endsWith(".svg")
    );
    if (svgFiles.length === 0) {
      setError("请选择SVG文件");
      return;
    }
    setBatchFiles(svgFiles);
  };

  // 批量转换
  const processBatchFiles = useCallback(async () => {
    if (batchFiles.length === 0) return;

    setIsBatchProcessing(true);
    setBatchProgress(0);

    const zip = new JSZip();

    for (let i = 0; i < batchFiles.length; i++) {
      const file = batchFiles[i];
      const reader = new FileReader();

      await new Promise((resolve) => {
        reader.onload = async (e) => {
          const content = e.target.result;
          const pngData = await convertSingleSvg(
            content,
            file.name.replace(".svg", "")
          );

          // 将PNG添加到zip
          const base64Data = pngData.split(",")[1];
          zip.file(
            `${file.name.replace(".svg", "")}.${outputFormat}`,
            base64Data,
            { base64: true }
          );

          setBatchProgress(((i + 1) / batchFiles.length) * 100);
          resolve();
        };
        reader.readAsText(file);
      });
    }

    // 生成并下载zip文件
    const content = await zip.generateAsync({ type: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(content);
    link.download = `converted_files_${Date.now()}.zip`;
    link.click();

    setIsBatchProcessing(false);
    setBatchFiles([]);
  }, [batchFiles, outputFormat]);

  // 添加到历史记录
  const addToHistory = useCallback(
    (name, content) => {
      const newHistory = {
        id: Date.now(),
        name: name,
        content: content,
        timestamp: new Date().toLocaleString(),
        settings: { scale, quality, backgroundColor, outputFormat },
      };
      setHistory((prev) => [newHistory, ...prev.slice(0, 9)]); // 保留最近10条
    },
    [scale, quality, backgroundColor, outputFormat]
  );

  // 从历史记录加载
  const loadFromHistory = useCallback((item) => {
    setSvgContent(item.content);
    setFileName(item.name.replace(".svg", ""));
    // 恢复设置
    if (item.settings) {
      setScale(item.settings.scale || 1);
      setQuality(item.settings.quality || 90);
      setBackgroundColor(item.settings.backgroundColor || "transparent");
      setOutputFormat(item.settings.outputFormat || "png");
    }
    parseSvgSize(item.content);
  }, []);

  // 应用模板
  const applyTemplate = useCallback((template) => {
    setScale(template.scale);
    setQuality(template.quality);
    setBackgroundColor(template.backgroundColor);
    setOutputFormat(template.format || "png");
    if (template.dpi) setDpi(template.dpi);
  }, []);

  // 解析SVG尺寸
  const parseSvgSize = useCallback((svgString) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, "image/svg+xml");
      const svgElement = doc.querySelector("svg");

      if (svgElement) {
        let width = svgElement.getAttribute("width");
        let height = svgElement.getAttribute("height");

        if (!width || !height) {
          const viewBox = svgElement.getAttribute("viewBox");
          if (viewBox) {
            const parts = viewBox.split(" ");
            if (parts.length >= 4) {
              width = parts[2];
              height = parts[3];
            }
          }
        }

        width = width ? parseFloat(width) : 400;
        height = height ? parseFloat(height) : 400;

        setOriginalSize({ width, height });
        updateOutputSize(width, height);
      }
    } catch (err) {
      console.error("解析SVG尺寸失败:", err);
    }
  }, []);

  // 更新输出尺寸
  const updateOutputSize = useCallback(
    (width, height) => {
      const dpiScale = dpi / 96; // 标准DPI是96
      const scaledWidth = Math.round(width * scale * dpiScale);
      const scaledHeight = Math.round(height * scale * dpiScale);
      setOutputSize({ width: scaledWidth, height: scaledHeight });
    },
    [dpi, scale]
  );

  // 拖拽处理
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.classList.add("dragover");
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragover");
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.currentTarget.classList.remove("dragover");
      const files = e.dataTransfer.files;
      if (activeTab === "batch") {
        handleBatchFileUpload(files);
      } else {
        if (files.length > 0) {
          handleFileUpload(files[0]);
        }
      }
    },
    [activeTab]
  );

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  }, []);

  const handleBatchFileSelect = useCallback((e) => {
    handleBatchFileUpload(e.target.files);
  }, []);

  const handleSvgCodeChange = useCallback(
    (e) => {
      const content = e.target.value;
      setSvgContent(content);
      if (content.trim()) {
        parseSvgSize(content);
        addToHistory("手动输入", content);
      }
    },
    [parseSvgSize, addToHistory]
  );

  const getBackgroundColor = useCallback(() => {
    switch (backgroundColor) {
      case "transparent":
        return "transparent";
      case "white":
        return "#ffffff";
      case "custom":
        return customBgColor;
      default:
        return "transparent";
    }
  }, [backgroundColor, customBgColor]);

  // 单个SVG转换函数
  const convertSingleSvg = useCallback(
    async (svgString, name) => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      canvas.width = outputSize.width;
      canvas.height = outputSize.height;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const bgColor = getBackgroundColor();
      if (bgColor !== "transparent") {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            ctx.drawImage(img, 0, 0, outputSize.width, outputSize.height);

            let mimeType = "image/png";
            if (outputFormat === "jpg") {
              mimeType = "image/jpeg";
            } else if (outputFormat === "webp") {
              mimeType = "image/webp";
            }

            const dataUrl = canvas.toDataURL(mimeType, quality / 100);
            URL.revokeObjectURL(url);
            resolve(dataUrl);
          } catch (err) {
            URL.revokeObjectURL(url);
            reject(err);
          }
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("SVG图像加载失败"));
        };

        img.src = url;
      });
    },
    [outputSize, getBackgroundColor, outputFormat, quality]
  );

  // 主转换函数
  const convertSvgToPng = useCallback(async () => {
    if (!svgContent.trim()) {
      setError("请先输入SVG内容");
      return;
    }

    setIsConverting(true);
    setError("");

    try {
      const dataUrl = await convertSingleSvg(svgContent, fileName);
      setPngDataUrl(dataUrl);
      setIsConverting(false);

      if (autoDownload) {
        setTimeout(downloadPng, 500);
      }
    } catch (err) {
      setError(err.message);
      setIsConverting(false);
      setPngDataUrl("");
    }
  }, [svgContent, fileName, convertSingleSvg, autoDownload]);

  const downloadPng = useCallback(() => {
    if (!pngDataUrl) return;

    const link = document.createElement("a");
    const extension =
      outputFormat === "jpg" ? "jpg" : outputFormat === "webp" ? "webp" : "png";
    link.download = fileName
      ? `${fileName}.${extension}`
      : `converted.${extension}`;
    link.href = pngDataUrl;
    link.click();
  }, [pngDataUrl, fileName, outputFormat]);

  const loadExampleSvg = useCallback(() => {
    const exampleSvg = `<svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#ff6b6b;stop-opacity:1" />
      <stop offset="50%" style="stop-color:#4ecdc4;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#45b7d1;stop-opacity:1" />
    </linearGradient>
    <filter id="shadow">
      <feDropShadow dx="2" dy="2" stdDeviation="3" flood-opacity="0.3"/>
    </filter>
  </defs>
  <rect x="10" y="10" width="280" height="180" rx="15" fill="url(#grad1)" filter="url(#shadow)"/>
  <circle cx="80" cy="100" r="40" fill="rgba(255,255,255,0.8)"/>
  <rect x="150" y="60" width="100" height="80" rx="10" fill="rgba(255,255,255,0.8)"/>
  <text x="150" y="150" text-anchor="middle" fill="white" font-size="16" font-family="Arial" font-weight="bold">专业SVG示例</text>
</svg>`;
    setSvgContent(exampleSvg);
    setFileName("专业示例");
    parseSvgSize(exampleSvg);
    addToHistory("示例.svg", exampleSvg);
  }, [parseSvgSize, addToHistory]);

  // 监听设置变化，自动更新输出尺寸
  useEffect(() => {
    if (originalSize.width > 0 && originalSize.height > 0) {
      updateOutputSize(originalSize.width, originalSize.height);
    }
  }, [scale, dpi, originalSize, updateOutputSize]);

  // 监听SVG内容变化，自动转换
  useEffect(() => {
    if (svgContent.trim() && originalSize.width > 0) {
      const timeoutId = setTimeout(() => {
        convertSvgToPng();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [
    svgContent,
    scale,
    backgroundColor,
    quality,
    customBgColor,
    outputFormat,
    dpi,
    convertSvgToPng,
    originalSize.width,
  ]);

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* 标题 */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-primary-100 p-3 rounded-full">
              <i className="fas fa-exchange-alt text-primary-600 text-2xl"></i>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            SVG转PNG工具 - 专业增强版
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            支持批量处理、多种格式、历史记录和模板功能的专业转换工具
          </p>
        </div>

        {/* 功能标签页 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-wrap border-b border-gray-200">
            <button
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === "upload"
                  ? "text-primary-600 border-b-2 border-primary-600 bg-primary-50"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("upload")}
            >
              <i className="fas fa-upload mr-2"></i>单文件
            </button>
            <button
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === "batch"
                  ? "text-primary-600 border-b-2 border-primary-600 bg-primary-50"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("batch")}
            >
              <i className="fas fa-layer-group mr-2"></i>批量处理
            </button>
            <button
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === "code"
                  ? "text-primary-600 border-b-2 border-primary-600 bg-primary-50"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("code")}
            >
              <i className="fas fa-code mr-2"></i>代码输入
            </button>
            <button
              className={`px-6 py-4 font-medium transition-colors ${
                showHistory
                  ? "text-primary-600 border-b-2 border-primary-600 bg-primary-50"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setShowHistory(!showHistory)}
            >
              <i className="fas fa-history mr-2"></i>历史记录
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
          {/* 左侧：输入区域 */}
          <div className="xl:col-span-1 space-y-6">
            {activeTab === "upload" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  上传SVG文件
                </h3>
                <div
                  className="drop-zone border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-all"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="flex flex-col items-center">
                    <i className="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-4"></i>
                    <p className="text-gray-600 mb-2 font-medium">
                      拖拽SVG文件到此处
                    </p>
                    <p className="text-sm text-gray-500 mb-4">或点击选择文件</p>
                    <div className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                      <i className="fas fa-folder-open mr-2"></i>
                      选择文件
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".svg"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>
            )}

            {activeTab === "batch" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  批量上传SVG文件
                </h3>
                <div
                  className="drop-zone border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-all"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() =>
                    document.getElementById("batchFileInput").click()
                  }
                >
                  <div className="flex flex-col items-center">
                    <i className="fas fa-layer-group text-4xl text-gray-400 mb-4"></i>
                    <p className="text-gray-600 mb-2 font-medium">
                      拖拽多个SVG文件到此处
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      支持批量转换和打包下载
                    </p>
                    <div className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                      <i className="fas fa-folder-open mr-2"></i>
                      选择多个文件
                    </div>
                  </div>
                  <input
                    id="batchFileInput"
                    type="file"
                    accept=".svg"
                    multiple
                    onChange={handleBatchFileSelect}
                    className="hidden"
                  />
                </div>

                {batchFiles.length > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        已选择 {batchFiles.length} 个文件
                      </span>
                      <button
                        onClick={processBatchFiles}
                        disabled={isBatchProcessing}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors text-sm"
                      >
                        {isBatchProcessing ? "处理中..." : "开始批量转换"}
                      </button>
                    </div>

                    {isBatchProcessing && (
                      <div className="mt-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>处理进度</span>
                          <span>{Math.round(batchProgress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full progress-bar"
                            style={{ width: `${batchProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    <div className="mt-3 max-h-32 overflow-y-auto">
                      {batchFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-sm"
                        >
                          <span className="truncate">{file.name}</span>
                          <span className="text-gray-500">
                            {(file.size / 1024).toFixed(1)}KB
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "code" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    输入SVG代码
                  </h3>
                  <button
                    onClick={loadExampleSvg}
                    className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    <i className="fas fa-magic mr-1.5"></i>示例
                  </button>
                </div>
                <textarea
                  value={svgContent}
                  onChange={handleSvgCodeChange}
                  placeholder="在此粘贴SVG代码..."
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            {showHistory && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    转换历史
                  </h3>
                  <button
                    onClick={() => setHistory([])}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    清空历史
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">
                      暂无历史记录
                    </p>
                  ) : (
                    history.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => loadFromHistory(item)}
                      >
                        <div>
                          <div className="font-medium text-gray-800">
                            {item.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.timestamp}
                          </div>
                        </div>
                        <i className="fas fa-redo text-gray-400"></i>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* 自定义设置 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-6 text-gray-800 flex items-center">
                <i className="fas fa-cog mr-2 text-primary-600"></i>
                转换设置
              </h3>

              {/* 输出格式 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  输出格式
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      outputFormat === "png"
                        ? "bg-primary-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setOutputFormat("png")}
                  >
                    PNG
                  </button>
                  <button
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      outputFormat === "jpg"
                        ? "bg-primary-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setOutputFormat("jpg")}
                  >
                    JPG
                  </button>
                  <button
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      outputFormat === "webp"
                        ? "bg-primary-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setOutputFormat("webp")}
                  >
                    WebP
                  </button>
                </div>
              </div>

              {/* DPI设置 */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    DPI设置
                  </label>
                  <span className="text-sm font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded">
                    {dpi} DPI
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <button
                    className={`py-1.5 px-2 rounded text-xs font-medium transition-all ${
                      dpi === 72
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setDpi(72)}
                  >
                    屏幕 (72)
                  </button>
                  <button
                    className={`py-1.5 px-2 rounded text-xs font-medium transition-all ${
                      dpi === 96
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setDpi(96)}
                  >
                    标准 (96)
                  </button>
                  <button
                    className={`py-1.5 px-2 rounded text-xs font-medium transition-all ${
                      dpi === 300
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setDpi(300)}
                  >
                    打印 (300)
                  </button>
                </div>
              </div>

              {/* 缩放比例 */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    缩放比例
                  </label>
                  <span className="text-sm font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded">
                    {scale}x
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500">0.1x</span>
                  <input
                    type="range"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={scale}
                    onChange={(e) => setScale(parseFloat(e.target.value))}
                    className="flex-1 slider-thumb h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-xs text-gray-500">5x</span>
                </div>
              </div>

              {/* 背景颜色 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  背景颜色
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      backgroundColor === "transparent"
                        ? "bg-primary-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setBackgroundColor("transparent")}
                  >
                    透明
                  </button>
                  <button
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      backgroundColor === "white"
                        ? "bg-primary-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setBackgroundColor("white")}
                  >
                    白色
                  </button>
                  <button
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      backgroundColor === "custom"
                        ? "bg-primary-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setBackgroundColor("custom")}
                  >
                    自定义
                  </button>
                </div>
                {backgroundColor === "custom" && (
                  <div className="mt-3 flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="color"
                      value={customBgColor}
                      onChange={(e) => setCustomBgColor(e.target.value)}
                      className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <span className="text-sm font-mono text-gray-600">
                      {customBgColor}
                    </span>
                  </div>
                )}
              </div>

              {/* 图片质量 */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    图片质量
                  </label>
                  <span className="text-sm font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded">
                    {quality}%
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500">10%</span>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="10"
                    value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                    className="flex-1 slider-thumb h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-xs text-gray-500">100%</span>
                </div>
              </div>

              {/* 模板选择 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  快速模板
                </label>
                <div className="space-y-2">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => applyTemplate(template)}
                      className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="font-medium text-gray-800">
                        {template.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {template.scale}x • {template.quality}% •{" "}
                        {template.backgroundColor}
                        {template.dpi && ` • ${template.dpi}DPI`}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 自动下载 */}
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="autoDownload"
                  checked={autoDownload}
                  onChange={(e) => setAutoDownload(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="autoDownload"
                  className="ml-3 block text-sm text-gray-700"
                >
                  转换完成后自动下载
                </label>
              </div>
            </div>

            {/* 错误信息 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center">
                  <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
                  <span className="text-red-700 font-medium">{error}</span>
                </div>
              </div>
            )}

            {/* 转换状态 */}
            {isConverting && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center">
                  <i className="fas fa-spinner fa-spin text-blue-500 mr-3"></i>
                  <span className="text-blue-700 font-medium">
                    正在转换中...
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* 右侧：预览区域 */}
          <div className="xl:col-span-2 space-y-6">
            {/* 尺寸信息 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center">
                <i className="fas fa-ruler-combined mr-2 text-primary-600"></i>
                尺寸信息
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
                  <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                    <i className="fas fa-file-image mr-2 text-blue-500"></i>
                    原始尺寸
                  </h4>
                  <p className="text-2xl font-bold text-blue-600">
                    {originalSize.width} × {originalSize.height} px
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200">
                  <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                    <i className="fas fa-expand-arrows-alt mr-2 text-green-500"></i>
                    输出尺寸
                  </h4>
                  <p className="text-2xl font-bold text-green-600">
                    {outputSize.width} × {outputSize.height} px
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    {dpi} DPI • {scale}x 缩放
                  </p>
                </div>
              </div>
            </div>

            {/* SVG预览 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <i className="fas fa-eye mr-2 text-primary-600"></i>
                  SVG预览
                </h3>
              </div>
              <div className="p-6">
                <div className="border border-gray-200 rounded-lg bg-white p-4 min-h-[200px] flex items-center justify-center">
                  {svgContent ? (
                    <div
                      className="flex justify-center items-center w-full"
                      dangerouslySetInnerHTML={{ __html: svgContent }}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <i className="fas fa-image text-4xl mb-3"></i>
                      <p className="text-gray-500">暂无SVG内容</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* PNG预览 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <i className="fas fa-download mr-2 text-primary-600"></i>
                  {outputFormat.toUpperCase()}预览
                </h3>
                {pngDataUrl && (
                  <button
                    onClick={downloadPng}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                  >
                    <i className="fas fa-download mr-2"></i>
                    下载{outputFormat.toUpperCase()}
                  </button>
                )}
              </div>
              <div className="p-6">
                <div className="border border-gray-200 rounded-lg bg-white p-4 min-h-[200px] flex items-center justify-center">
                  {pngDataUrl ? (
                    <div className="flex justify-center">
                      <img
                        src={pngDataUrl}
                        alt={`转换后的${outputFormat.toUpperCase()}`}
                        className="max-w-full h-auto border rounded-lg shadow-sm"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <i className="fas fa-image text-4xl mb-3"></i>
                      <p className="text-gray-500">
                        转换后的{outputFormat.toUpperCase()}将显示在这里
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 功能说明 */}
        <div className="mt-12 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-6 text-gray-800 flex items-center">
            <i className="fas fa-info-circle mr-2 text-primary-600"></i>
            新增功能特点
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-start p-4 bg-blue-50 rounded-lg">
              <div className="flex-shrink-0">
                <i className="fas fa-layer-group text-blue-500 text-xl mr-3"></i>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-1">批量处理</h4>
                <p className="text-sm text-gray-600">
                  支持多文件同时转换，打包下载
                </p>
              </div>
            </div>
            <div className="flex items-start p-4 bg-green-50 rounded-lg">
              <div className="flex-shrink-0">
                <i className="fas fa-file-export text-green-500 text-xl mr-3"></i>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-1">多格式支持</h4>
                <p className="text-sm text-gray-600">PNG、JPG、WebP格式输出</p>
              </div>
            </div>
            <div className="flex items-start p-4 bg-purple-50 rounded-lg">
              <div className="flex-shrink-0">
                <i className="fas fa-history text-purple-500 text-xl mr-3"></i>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-1">历史记录</h4>
                <p className="text-sm text-gray-600">
                  保存转换历史，快速重新转换
                </p>
              </div>
            </div>
            <div className="flex items-start p-4 bg-orange-50 rounded-lg">
              <div className="flex-shrink-0">
                <i className="fas fa-print text-orange-500 text-xl mr-3"></i>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-1">DPI设置</h4>
                <p className="text-sm text-gray-600">
                  支持屏幕、标准、打印质量
                </p>
              </div>
            </div>
            <div className="flex items-start p-4 bg-red-50 rounded-lg">
              <div className="flex-shrink-0">
                <i className="fas fa-magic text-red-500 text-xl mr-3"></i>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-1">快速模板</h4>
                <p className="text-sm text-gray-600">预设转换模板，一键应用</p>
              </div>
            </div>
            <div className="flex items-start p-4 bg-indigo-50 rounded-lg">
              <div className="flex-shrink-0">
                <i className="fas fa-tachometer-alt text-indigo-500 text-xl mr-3"></i>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-1">进度显示</h4>
                <p className="text-sm text-gray-600">批量处理时显示实时进度</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 隐藏的canvas用于转换 */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

export default SvgToPngConverter;
